import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getAvailableContainerImages } from '@/components-biz/ImageSelect/service';
import type { CommonImageItemType } from '@/components-biz/ImageSelect/types';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

export interface AppRepoImageQuery {
  cpu?: string;
}

/** 拉取应用仓库容器镜像列表（创建实例弹窗选择用，支持分页与搜索） */
export function useAppRepoImageList(queryObj: AppRepoImageQuery = {}) {
  const [items, setItems] = useState<CommonImageItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [total, setTotal] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const queryObjRef = useRef(queryObj);
  queryObjRef.current = queryObj;
  const queryKey = useMemo(() => JSON.stringify(queryObj ?? {}), [queryObj]);
  const clientModeRef = useRef(false);
  const allItemsRef = useRef<CommonImageItemType[]>([]);

  const applyClientPage = useCallback((list: CommonImageItemType[], nextPage: number, nextLimit: number, nextKeyword: string) => {
    const key = nextKeyword.trim().toLowerCase();
    const filtered = key
      ? list.filter((item) => {
          const name = String(item.name || '').toLowerCase();
          const vendor = String(item.vendor || '').toLowerCase();
          return name.includes(key) || vendor.includes(key);
        })
      : list;
    const start = (nextPage - 1) * nextLimit;
    setItems(filtered.slice(start, start + nextLimit));
    setTotal(filtered.length);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const trimmedKeyword = searchKeyword.trim();

        if (clientModeRef.current && allItemsRef.current.length > 0) {
          if (cancelled) return;
          applyClientPage(allItemsRef.current, page, limit, searchKeyword);
          return;
        }

        const result = await getAvailableContainerImages({
          ...queryObjRef.current,
          artifactType: 'ai_application',
          page,
          limit,
          ...(trimmedKeyword ? { 'name[like]': trimmedKeyword } : {}),
        });
        if (cancelled) return;

        const list = (result.items || []).map((item) => ({ ...item, key: item.id })) as CommonImageItemType[];
        const totalItems = typeof result.totalItems === 'number' ? result.totalItems : list.length;
        const serverIgnoresPagination = page === 1 && !trimmedKeyword && list.length > limit && totalItems <= list.length;

        if (serverIgnoresPagination) {
          clientModeRef.current = true;
          allItemsRef.current = list;
          applyClientPage(list, page, limit, searchKeyword);
          return;
        }

        clientModeRef.current = false;
        allItemsRef.current = [];
        setItems(list);
        setTotal(totalItems);
      } catch (error) {
        console.error('fetch app repo images failed', error);
        if (!cancelled) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applyClientPage, limit, page, searchKeyword]);

  useEffect(() => {
    clientModeRef.current = false;
    allItemsRef.current = [];
    setPage(DEFAULT_PAGE);
  }, [queryKey]);

  const onInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const onSearch = useCallback((value: string) => {
    clientModeRef.current = false;
    allItemsRef.current = [];
    setInputValue(value);
    setSearchKeyword(value.trim());
    setPage(DEFAULT_PAGE);
  }, []);

  const onPageChange = useCallback(
    (nextPage: number, nextPageSize?: number) => {
      setPage(nextPage);
      if (nextPageSize && nextPageSize !== limit) {
        setLimit(nextPageSize);
      }
    },
    [limit],
  );

  return {
    items,
    loading,
    page,
    limit,
    total,
    inputValue,
    onInputChange,
    onSearch,
    onPageChange,
  };
}
